import { Callout, SearchInput } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { DeploymentTargetResource, EnvironmentResource, ProjectResource, Repository, GetLiveStatusResponse, KubernetesLiveStatusResource, TimelineItem } from "@octopusdeploy/octopus-server-client";
import { HostingEnvironment, TaskState, Permission } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import * as React from "react";
import type { ViewLiveStatusPageEvent } from "~/analytics/Analytics";
import { useAnalyticLiveStatusPageDispatch } from "~/analytics/Analytics";
import type { LiveObjectStatusFilter } from "~/areas/projects/components/Observability/LiveStatusFiltering";
import { LiveStatusFiltering, defaultFilter, filterMachineResources } from "~/areas/projects/components/Observability/LiveStatusFiltering";
import { LiveStatusTable } from "~/areas/projects/components/Observability/LiveStatusTable";
import { LiveStatusObjectDrawer } from "~/areas/projects/components/Observability/ObjectDrawer/LiveStatusObjectDrawer";
import { DeploymentTimelineDrawer } from "~/areas/projects/components/Releases/Deployments/DeploymentTimelineDrawer";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { NotFound } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { LiveStatusSummaryBar } from "./LiveStatusSummaryBar";
export interface LoaderData {
    latestDeployment: TimelineItem;
    environment: EnvironmentResource;
    kubernetesLiveStatus: GetLiveStatusResponse;
    deploymentTargets: DeploymentTargetResource[];
    isRunningInHACluster: boolean;
}
type SharedLiveStatusPageProps = {
    loaderData: LoaderData;
    project: ProjectResource;
    tenantId?: string;
};
const refetchIntervalInMs = 1000;
const ExcludedTaskStates: string[] = [TaskState.Queued];
export function SharedLiveStatusPage({ loaderData, project, tenantId }: SharedLiveStatusPageProps) {
    const [filter, setFilter] = useState<LiveObjectStatusFilter>(defaultFilter);
    const [resourceInDrawer, setResourceInDrawer] = useState<KubernetesLiveStatusResource | undefined>(undefined);
    const [isTimelineDrawerOpen, setIsTimelineDrawerOpen] = useState<boolean>(false);
    const featureToggleEnabled = useOctopusFeatureToggle("kubernetes-monitor", false) && isFeatureToggleEnabled("KubernetesLiveObjectStatusFeatureToggle");
    const { result: { latestDeployment, kubernetesLiveStatus, deploymentTargets, isRunningInHACluster }, } = useQuery(async (repository) => refresh(repository, project.Id, loaderData.environment.Id, tenantId), [project.Id, loaderData.environment.Id, tenantId], "Live Status Page", {
        initialResult: loaderData,
        refetchIntervalInMs,
    });
    const liveStatusSummary = kubernetesLiveStatus.Summary;
    const resourcesToRender = filterMachineResources(kubernetesLiveStatus.MachineStatuses, filter);
    function updateFilterContent<K extends keyof LiveObjectStatusFilter>(updates: Pick<LiveObjectStatusFilter, K>) {
        setFilter({ ...defaultFilter, ...updates });
    }
    const showTimelineDrawer = () => setIsTimelineDrawerOpen(true);
    const additionalKindTypes = useMemo(() => [...new Set(kubernetesLiveStatus.MachineStatuses.flatMap((m) => m.Resources.map((r) => r.Kind)))], [kubernetesLiveStatus.MachineStatuses]);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const namespaceNames: string[] = useMemo(() => [...new Set(kubernetesLiveStatus.MachineStatuses.flatMap((m) => m.Resources.map((r) => r.Namespace as string)).filter((ns) => !!ns))], [kubernetesLiveStatus.MachineStatuses]);
    if (!featureToggleEnabled) {
        return <NotFound />;
    }
    const filters = !isRunningInHACluster
        ? {
            inputs: [<SearchInput autoFocus={true} value={filter.name} onChange={(x) => updateFilterContent({ name: x })} accessibleName={"search by object name"} placeholder={"Search by object name..."}/>],
            advancedFilters: {
                onResetFilter: () => setFilter(defaultFilter),
                hasUserSelectedValues: !isEqual(defaultFilter, filter),
                content: <LiveStatusFiltering filter={filter} setFilter={setFilter} additionalKindTypes={additionalKindTypes} namespaceNames={namespaceNames}/>,
            },
        }
        : undefined;
    return (<>
            <PageContent header={{
            title: `${project.Name} ${loaderData.environment.Name} Live Status`,
            pageActions: isAllowed({ permission: Permission.InterruptionView, project: project.Id, environment: "*", tenant: "*" })
                ? [{ type: "button", buttonType: "secondary", label: "Timeline", onClick: () => showTimelineDrawer() }]
                : undefined,
        }} filters={filters}>
                {isRunningInHACluster && <Callout type={"warning"} title={"Kubernetes Monitor is not supported in HA clusters"}/>}
                {!isRunningInHACluster && (<>
                        <LiveStatusSummaryBar spaceId={project.SpaceId} projectSlug={project.Slug} liveStatusSummary={liveStatusSummary} latestDeployment={latestDeployment}/>
                        <LiveStatusTable spaceId={project.SpaceId} deploymentTargets={deploymentTargets} machineLiveStatusResources={resourcesToRender} resourceSelected={(resource) => setResourceInDrawer(resource)}/>
                    </>)}
            </PageContent>
            <LiveStatusObjectDrawer environmentId={loaderData.environment.Id} projectId={project.Id} tenantId={tenantId} onClose={() => setResourceInDrawer(undefined)} resource={resourceInDrawer} latestDeployment={latestDeployment} analyticEventData={{
            releaseId: latestDeployment.ReleaseId,
            deploymentStatus: latestDeployment.TaskState,
            liveApplicationHealth: liveStatusSummary.Status,
            executionType: "deployment",
        }}/>
            <DeploymentTimelineDrawer isOpen={isTimelineDrawerOpen} onClose={() => setIsTimelineDrawerOpen(false)} deploymentName={project.Name} environmentId={loaderData.environment.Id} projectId={project.Id} spaceId={project.SpaceId} tenantId={tenantId}/>
            <LiveStatusPageAnalyticView eventData={{
            projectId: project.Id,
            releaseId: latestDeployment.ReleaseId,
            environmentId: loaderData.environment.Id,
            deploymentId: latestDeployment.Id,
            deploymentStatus: latestDeployment.TaskState,
            liveApplicationHealth: liveStatusSummary.Status,
        }}/>
        </>);
}
export async function sharedLiveStatusPageLoader(repository: Repository, environmentId: string, projectContext: Promise<ProjectContextProps>, tenantId?: string): Promise<LoaderData> {
    const { state: { model: project }, } = await projectContext;
    const refreshingData = await refresh(repository, project.Id, environmentId, tenantId);
    return {
        ...refreshingData,
        environment: await repository.Environments.get(environmentId),
    };
}
async function refresh(repository: Repository, projectId: string, environmentId: string, tenantId?: string) {
    // There was more complicated logic ensuring that tenantId was null before, I can't reproduce the issue so I'm leaving this here to ensure that I haven't missed an edge case
    const nullableTenantId: string | null = tenantId === undefined ? null : tenantId;
    const latestDeploymentPromise = getLatestTimelineItem(repository, projectId, environmentId, nullableTenantId);
    const kubernetesLiveStatusPromise = repository.KubernetesLiveStatus.getLiveStatus(projectId, environmentId, tenantId);
    const serverNodesPromise = repository.OctopusServerNodes.all();
    const isSelfHosted = (await repository.Licenses.getCurrentStatus()).HostingEnvironment === HostingEnvironment.SelfHosted;
    const kubernetesLiveStatus = await kubernetesLiveStatusPromise;
    return {
        latestDeployment: await latestDeploymentPromise,
        kubernetesLiveStatus,
        deploymentTargets: await repository.Machines.all({ ids: kubernetesLiveStatus.MachineStatuses.map((r) => r.MachineId) }),
        isRunningInHACluster: isSelfHosted && (await serverNodesPromise).length > 1,
    };
}
async function getLatestTimelineItem(repository: Repository, projectId: string, environmentId: string, tenantId: string | null) {
    // We only need the latest timeline item that is not in the ServerTaskState.Queued
    // The BFF handler already does the sorting for us so we can just request one item
    const deploymentsTimeline = await repository.Deployments.getDeploymentTimeline(projectId, environmentId, tenantId || undefined, 0, 1, ExcludedTaskStates);
    return deploymentsTimeline.TimelineItems.Items[0];
}
function LiveStatusPageAnalyticView({ eventData }: {
    eventData: ViewLiveStatusPageEvent;
}) {
    const dispatch = useAnalyticLiveStatusPageDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch("View Live Status Page", eventData), []);
    return <></>;
}
